import React from 'react';
import { Box, CircularProgress, Fade, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';

interface AppPreloaderProps {
  message?: string;
  showProgress?: boolean;
  variant?: 'splash' | 'minimal' | 'skeleton';
}

/**
 * Optimized preloader component with multiple variants
 * Best practices:
 * - Fade-in animations for smooth UX
 * - Multiple variants for different loading states
 * - Responsive design
 * - Accessible with proper ARIA labels
 */
export const AppPreloader: React.FC<AppPreloaderProps> = ({ 
  message, 
  showProgress = false,
  variant = 'splash' 
}) => {
  if (variant === 'minimal') {
    return (
      <Fade in timeout={300}>
        <Box
          role="progressbar"
          aria-label="Loading"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <LinearProgress />
        </Box>
      </Fade>
    );
  }

  if (variant === 'skeleton') {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            background: (theme) => theme.palette.background.default,
          }}
        >
          <Stack spacing={3} alignItems="center" sx={{ maxWidth: 600, width: '100%' }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width="80%" height={30} />
            <Skeleton variant="text" width="70%" height={30} />
          </Stack>
        </Box>
      </Fade>
    );
  }

  // Default 'splash' variant
  return (
    <Fade in timeout={300}>
      <Box
        role="progressbar"
        aria-label={message || "Loading application"}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: (theme) => theme.palette.background.default,
          px: 3,
        }}
      >
        <Stack spacing={3} alignItems="center" sx={{ maxWidth: 400, width: '100%' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{
              color: (theme) => theme.palette.primary.main,
            }}
          />
          
          {message && (
            <Typography 
              variant="body1" 
              color="text.secondary"
              textAlign="center"
              sx={{
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.6,
                  },
                },
              }}
            >
              {message}
            </Typography>
          )}

          {showProgress && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}
        </Stack>
      </Box>
    </Fade>
  );
};
